/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */
import React, { useCallback, useEffect, useState, useRef, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, CircularProgress, Tabs } from '@mui/material';
import moment, { MomentInput } from 'moment';
import { t } from 'i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  BidStatus,
  JobAttachment,
  JobBidVendor,
  JobDueDate,
  JobFacilityWithOnlyId,
  JobFrequency,
  JobStatus,
  JobType,
  ServiceCategoryV3,
  UserDetailsInput,
  WorkDayTeamStatus,
  WorkOrder,
  WorkOrderFacilityAccess,
} from '../../../../API';

import FacilityApiService from '../../../Facility/Services/FacilityApiService';
import {
  getStoredCustomerDetails,
  isMock,
  ParseFloat,
  checkImageValid,
  getAuthDetails,
  WorkOrderStatus,
  getAuthData,
  formatDate,
  JobExecution,
  JobDetails,
  UserRoles,
  hasAccessToConversations,
} from '../../../../Shared/Utilities/utils';
import { debounce, isEmpty } from 'lodash';
import WorkOrdersApiService from '../../Services/WorkOrdersService';
import JobCreateApiService from '../../Services/JobCreateService';
import MasterDataApiService from '../../../../Shared/Services/MasterDataService';
import { Loader } from '../JobView/JobsView.styles';
import { ErrorMessage, ErrorText } from '../../../../Shared/Components/Common/ErrorMessage/ErrorMessage';
import { NoDataFound } from '../../../../Shared/Components/Common/NoDataFound/NoDataFound';

import { Storage } from 'aws-amplify';
import theme from '../../../../Shared/Themes/theme';

import {
  DraftStatus,
  DraftStep,
  EMPTY_VALUE,
  FilterName,
  FormFieldType,
  states,
  UserType,
  velocitiOrganizationId,
  WORK_TICKET_STATUS_LABEL,
} from '../../../../Shared/Constants/App';
import {
  FacilityAccessData,
  IJobData,
  IPerformanceMetrics,
  IVendorDetails,
  JobSummaryV2Data,
  ShiftTypes,
} from '../../Models/JobDetailsOverview.Model';
import { Icons } from '../../../../Shared/Constants/Icons';

import {
  PerformanceMetricsWrapper,
  ProgressBar,
  ProgressBarWrapper,
  Card,
  CardWrapper,
  TaskStatusWrapper,
  StatusWrapper,
  ProgressBarContainer,
  Timings,
  AssociateTimingWrapper,
  AssociateDetails,
  TabTitle,
  TabsWrapper,
  TabSection,
  FacilityInfoWrapper,
  FacilityDetails,
  FacilityJobName,
  FacilityDuration,
  FacilityShiftDetails,
  FacilityShiftList,
  ShiftTime,
  ShiftType,
  VendorEmail,
  VendorInfoWrapper,
  VendorInfo,
  IconWrapper,
  PersonIcon,
  NameWrapper,
  PersonName,
  Name,
  DropdownWrapper,
  PerformanceMetrics,
  DateWrapper,
  FacilityDurationWrapper,
  TitleText,
  TitleStatus,
  IssuesRaised,
  OneTimeJobs,
  DateContainer,
  ChangeDate,
  PieChartScore,
  ClockWrapper,
  AssociateDurationDetails,
  ProgressLoader,
  DaysRange,
  ConversationStyles,
  AccessPinValue,
  AccessPin,
  AccessPinWrapper,
  LoaderWrapper,
  ChatButtonContainer,
  ShiftAndChatContainer,
  SupervisorWrapper,
  JobDetailsWrapper,
  JobDetailLabel,
} from './JobDetailsOverview.styles';
import ActionItems from '../ActionItems/ActionItems';
import AssociateCard from '../AssociateOnTheJob/AssociateCard';
import SimpleDatePicker from '../../../../Shared/Components/Common/SimpleDatePicker/SimpleDatePicker';
import { CustomerContainer, NoDataWrapper } from '../../../../Shared/Components/Layout/styles';
import PageTitle from '../../../../Shared/Components/Common/PageTitle/PageTitle';
import JobDetailsTasks from '../WorkTasks/WorkTasks';
import { useForm, useWatch } from 'react-hook-form';
import { ConversationName } from '../../../../Shared/Components/Conversations/ConversationsScreen/Conversations.Style';
import { OrganizationService } from '../../../../Shared/Services/OrganizationService';
import { IConversationContext } from '../PostJobAwardConversations/PostJobAwardConversations';
import CustomerApiService from '../../../Customer/Services/CustomerService';
import { VendorType } from '../../../../Shared/Models/Vendors.Models';
import { IJobOverview } from '../JobsListView/JobsListView';
import SearchableDropDown from '../../../../Shared/Components/Common/CustomDropDown/SearchableDropDown';
import { getJobDetailsTab } from '../../../Vendor/Models/constants';
import { getSchedulePattern } from '../../Utilities/JobDetailsOverview.Utils';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}>
      {value === index && children}
    </div>
  );
}

export const renderImg = (type: ShiftTypes) => {
  if (ShiftTypes.Day === type) {
    return <img src={Icons.DayIcon} alt={t('altTexts:day')} />;
  } else if (ShiftTypes.DayAndNight === type) {
    return <img src={Icons.DayAndNightIcon} alt={t('altTexts:dayAndNight')} />;
  } else if (ShiftTypes.Night === type) {
    return <img src={Icons.NightIcon} alt={t('altTexts:night')} />;
  }
  return null;
};

/**
 *
 * @returns JobDetailsOverview
 */

export interface JobWithDueDates {
  jobId: string;
  actualId?: string | null;
  jobName: string;
  name: string;
  startDate?: string | null;
  actualStartDate?: string | null;
  actualEndDate?: string | null;
  stopDate?: string | null;
  jobStartDate?: string | null;
  jobEndDate?: string | null;
  dataStatus?: string | null;
  jobType?: string | null;
  specialInstructions?: string | null;
  draftStep?: DraftStep | null;
  draftStatus?: DraftStatus | null;
  createdOn?: string | null;
  createdBy?: string | null;
  modifiedOn?: string | null;
  modifiedBy?: string | null;
  createdByName?: string | null;
  modifiedByName?: string | null;
  bidCount?: number | null;
  jobCustodian?: string | null;
  intId?: string | null;
  allowVendorToReschedule?: boolean | null;
  jobFacilities?: Array<JobFacilityWithOnlyId | null> | null;
  jobDueDates?: Array<JobDueDate | null> | null;
  jobBids?: Array<JobBidVendor | null> | null;
  jobFrequencies?: Array<JobFrequency | null> | null;
  jobAttachments?: Array<JobAttachment | null> | null;
  vendorId?: string | null | undefined;
  facilities?: Array<WorkOrderFacilityAccess | null> | null;
  workOrderId?: string;
  customerId?: string;
  status?: string;
  parentWorkOrderId?: string;
  subWorkOrders?: any;
  supervisorName?: string;
  supervisorEmail?: string;
  totalAmount?: number;
  jobBidSummary?: { jobDetails: { intId: string } };
  shiftDetails?: Array<{ woScheduleMasterId: string } | null> | null;
  workTicketStatus?: string;
}

// eslint-disable-next-line sonarjs/cognitive-complexity
const JobDetailsOverview = React.memo(() => {
  const { t } = useTranslation(['dashboard', 'altTexts', 'conversations']);
  const [selectedFacility, setSelectedFacility] = useState<IJobData>();
  const location = useLocation();
  const navigate = useNavigate();
  const jobDetailsTab = getJobDetailsTab();
  const pathVariables = location?.pathname.split('/');
  const initialTabIndex = jobDetailsTab.findIndex((tab) => tab.route === pathVariables[pathVariables.length - 1]);
  const [tabSelected, setTabSelected] = useState(initialTabIndex === -1 ? 0 : initialTabIndex);
  const newDate = location?.state?.selectedDate;
  const [allJobs, setAllJobs] = useState<any>([]);
  const [isServicesLoading, setIsServicesLoading] = useState<boolean>(true);
  const [facilityId, setFacilityId] = useState('');
  const [selectedJob, setSelectedJob] = useState<string>('');
  const [selectedJobDetails, setSelectedJobDetails] = useState<JobSummaryV2Data>();
  const [vendorDetails, setVendorDetails] = useState<IVendorDetails>();
  const [frequencies, setFrequencies] = useState([]);
  const [vendorLoading, setVendorLoading] = useState<boolean>(true);
  const [isVendorQueryError, setIsVendorQueryError] = useState<boolean>(false);
  const [isJobSummaryError, setIsJobSummaryError] = useState<boolean>(false);
  const isAnalyticsAPIError: boolean = false;
  const [jobsLoading, setJobsLoading] = useState<boolean>(true);

  const [screenLoading, setScreenLoading] = useState<boolean>(true);
  const performanceMetrics: IPerformanceMetrics[] = [];
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [status, setStatus] = useState();
  const [originalStatus, setOriginalStatus] = useState();

  const performanceLoader = false;
  const [startStopDate, setStartStopDate] = useState<any>({});
  const [vendorTypes, setVendorTypes] = useState<any>({});
  const validImgRef = useRef(false);

  const [jobType, setJobType] = useState('');

  const { startDate, stopDate } = startStopDate;
  const isDateLoading = !startDate || !stopDate;

  const storedAuthDetails = getAuthDetails();
  const { role, isDistrictManager } = getAuthData() || {};
  const CUSTOM_ROLE_KEY = 'custom:Role';
  const { userId = '', customerId = '' } = getStoredCustomerDetails();

  const [selectedFacilityData, setSelectedFacilityData] = useState();
  const [selectedJobData, setSelectedJobData] = useState<WorkOrder>();
  const [jobStartDate, setJobStartDate] = useState<string>('');
  const [jobEndDate, setJobEndDate] = useState<string>('');

  const loggedInUserInternalId =
    storedAuthDetails['custom:UserId'] ?? storedAuthDetails['preferred_username'] ?? userId;
  const loggedInUserName = storedAuthDetails['name'];
  const [isConversationsDataLoading, setIsConversationsDataLoading] = useState<boolean>(false);
  const [jobDetails, setJobDetails] = useState<IJobOverview>();

  const [searchString, setSearchString] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  const [jobName, setJobName] = useState('');

  const [originalJobName, setOriginalJobName] = useState('');

  const [selectedOriginalJobData, setSelectedOriginalJobData] = useState<WorkOrder>();

  const [selectedOriginalJobDetails, setOriginalSelectedJobDetails] = useState<JobSummaryV2Data>();

  const [originalFacilityId, setOriginalFacilityId] = useState('');

  const [workOrderResponseData, setWorkOrderResponseData] = useState();

  const [jobSummaryResponseData, setJobSummaryResponseData] = useState();

  const [facilityAccessDetails, setFacilityAccessDetails] = useState<FacilityAccessData>();
  const [isFacilityAccessLoader, setIsFacilityAccessLoader] = useState(false);

  const [futureShiftDates, setFutureShiftDates] = useState<string[]>([]);

  const [supervisorName, setSupervisorName] = useState('');
  const [supervisorEmail, setSupervisorEmail] = useState('');
  const [isPrimeVendor, setIsPrimeVendor] = useState<boolean>(false);
  const [parentWoScheduleMasterId, setParentWoScheduleMasterId] = useState<string>('');

  const isJobManagement = location?.state?.isJobManagement;
  const { chatWithTeamMember } = ConversationStyles;
  const { control, setValue } = useForm({
    defaultValues: {
      specificDate: newDate ? newDate : new Date().toISOString(),
    },
  });

  const specificDate = useWatch({
    control,
    name: 'specificDate',
  });

  const isFirstLoad = useRef(true);
  const hasFetchedInitially = useRef(false);
  const isFirstJobsDataLoad = useRef(true);

  useEffect(() => {
    if (location?.state?.scheduleDate || location?.state?.selectedDate) {
      setValue('specificDate', location?.state?.scheduleDate ?? location?.state?.selectedDate);
      return;
    }
    if (!selectedJobData) return;
    const {
      actualStartDate = '',
      actualEndDate = '',
      jobStartDate = '',
      jobEndDate = '',
    } = selectedJobData || { actualStartDate: '', actualEndDate: '', jobStartDate: '', jobEndDate: '' };
    if ((actualStartDate && actualEndDate) || (jobStartDate && jobEndDate)) {
      let startDate: string = actualStartDate || jobStartDate || '';
      startDate = startDate.endsWith('Z') ? startDate.slice(0, startDate?.length - 1) : startDate;
      const endDate: string = actualEndDate || jobEndDate || '';
      const isCurrentJobInTheRangeOfJobDurationForRecurring = moment().isBetween(startDate, endDate);
      if (
        (selectedJobData?.jobType === JobType.Recurring || selectedJobData?.jobType === JobType.Periodic) &&
        isCurrentJobInTheRangeOfJobDurationForRecurring
      ) {
        let currentDeviceDate = new Date()?.toJSON();
        currentDeviceDate = currentDeviceDate?.slice(0, currentDeviceDate?.length - 1);
        setValue('specificDate', currentDeviceDate);
      } else {
        setValue('specificDate', startDate);
      }
    }
  }, [selectedJobData]);

  const handleChangeTabs = useCallback((event: React.SyntheticEvent, newValue: number) => {
    setTabSelected(newValue);
    navigate(
      `/job-detail/${newValue === 0 ? 'overview' : newValue === 1 ? 'worktasks' : newValue === 2 ? 'associateOnTheJob' : 'actionItems'}`,
      {
        state: { ...location.state },
      }
    );
  }, []);

  const fetchServices = async (jobId: string, facilityCustomerId: string) => {
    setIsServicesLoading(true);
    let { customerId = '' } = getStoredCustomerDetails() || {};

    if (storedAuthDetails[CUSTOM_ROLE_KEY] === UserType.Vendor) {
      customerId = facilityCustomerId;
    }

    try {
      const serviceCategories = await MasterDataApiService.getAllServiceCategoryV3(customerId as string);
      if (serviceCategories.data) {
        if (!isEmpty(jobSummaryResponseData)) {
          const { jobFrequencies, jobType, startDate, stopDate, jobVendors } = jobSummaryResponseData ?? {
            jobFrequencies: [],
            jobType: '',
            startDate: '',
            stopDate: '',
            jobVendors: [],
          };
          setVendorTypes(jobVendors);
          setJobType(jobType as SetStateAction<string>);

          const frequencies = jobFrequencies.map((frequency: any) => {
            const serviceList = frequency.mdServiceId.map((item: string) => {
              const category = serviceCategories?.data.find((categoryItem: ServiceCategoryV3) => {
                return categoryItem.serviceCategoryId === item;
              });
              return {
                categoryId: category?.serviceCategoryId ?? '',
                categoryName: category?.serviceCategoryName ?? '',
                icon: category?.categoryImageUrl ?? '',
              };
            });

            return {
              shiftType: frequency.mdShiftType,
              shiftTiming: frequency.mdScheduleType,
              schedulePattern: getSchedulePattern(frequency),
              services: serviceList,
            };
          });

          const servicesList: any = [];
          Array.isArray(frequencies) &&
            frequencies.forEach((freq: any) => {
              const frequency = freq.services.map((service: any) => {
                return {
                  serviceName: service.categoryName,
                  mdServiceId: service.categoryId,
                  iconUrl: service.icon,
                  shiftType: freq.shiftType,
                  shiftTiming: freq.shiftTiming,
                };
              });
              frequency.forEach((dataItem: any) => {
                servicesList.push({
                  ...dataItem,
                  // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                  id: servicesList.length + 1,
                });
              });
            });

          setFrequencies(frequencies as any);
          setStartStopDate({ startDate, stopDate });
          setIsJobSummaryError(false);
        } else {
          setIsJobSummaryError(true);
        }
      } else {
        setIsJobSummaryError(true);
        console.error('serviceCategoryV2 queries: ', serviceCategories.errors);
      }
    } catch (e) {
      console.error('Error', e);
    } finally {
      setIsServicesLoading(false);
    }
  };

  const getVendorDetailsComp = (vendorDetails: {
    vendorPersonIcon: string;
    vendorPersonName: string;
    vendorName: string;
    vendorLocation: string;
    VendorPersonMailId: string;
    vendorContactNumber: string;
  }) => {
    const { vendorPersonIcon, vendorPersonName, vendorName } = vendorDetails;
    checkImageValid(
      vendorPersonIcon,
      function () {
        validImgRef.current = true;
      },
      function () {
        validImgRef.current = false;
      }
    );

    const vendorImgUrl = validImgRef.current ? vendorPersonIcon : Icons.VendorDetailsLogo;

    return (
      <VendorInfoWrapper>
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          <PersonIcon>
            <img src={vendorImgUrl} alt={t('altTexts:vendorImage')} />
          </PersonIcon>
          <Box sx={{ display: 'flex', paddingLeft: '1rem', width: '100%', alignItems: 'center' }}>
            <Box sx={{ flex: 1 }}>
              <PersonName>{vendorPersonName}</PersonName>
              <Name>{vendorName}</Name>
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          {supervisorName && (
            <Box sx={{ flex: 1 }}>
              <Box
                sx={{ display: 'flex', padding: '0.5rem', width: '100%', alignItems: 'center', flexDirection: 'row' }}>
                <Name>{t('dashboard:supervisorName')} &nbsp;</Name>
                <SupervisorWrapper>{supervisorName}</SupervisorWrapper>
              </Box>
            </Box>
          )}
          {supervisorEmail && (
            <Box sx={{ flex: 1 }}>
              <VendorEmail>
                <VendorInfo>
                  <img src={Icons.EmailIcon} alt={t('altTexts:vendorEmail')} />
                  <p>{supervisorEmail}</p>
                </VendorInfo>
              </VendorEmail>
            </Box>
          )}
        </Box>
      </VendorInfoWrapper>
    );
  };

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const getJobsResponse = async (customerId: string, pageNumber?: number, searchText?: string) => {
    if (!hasFetchedInitially.current && isEmpty(searchString) && isEmpty(searchText)) {
      hasFetchedInitially.current = true;
      const allOngoingInProgressJobsResponse =
        selectedJob || location?.state?.jobId
          ? await WorkOrdersApiService.fetchAllWorkOrders({
              jobId: selectedJob || location?.state?.jobId,
            })
          : { data: [], metadata: [], errors: [] };
      if (!isEmpty(allOngoingInProgressJobsResponse?.data)) {
        setWorkOrderResponseData(allOngoingInProgressJobsResponse?.data);
        fetchUpcomingShifts(allOngoingInProgressJobsResponse?.data?.[0]);
      }
      setTotalCount(allOngoingInProgressJobsResponse?.metadata?.totalCount);

      return allOngoingInProgressJobsResponse;
    }

    const { userId = '' } = getStoredCustomerDetails() || {};

    const authInfo = localStorage.getItem('auth');
    const parsedAuthInfo = authInfo ? JSON.parse(authInfo) : {};

    if ((role as UserType) === UserType.Vendor) {
      const { customerId }: { customerId: string } = getStoredCustomerDetails() || '';

      let jobCreatorRoleName = '';

      if (!isEmpty(userId)) {
        if (!isEmpty(parsedAuthInfo?.roles || [])) {
          if (parsedAuthInfo?.roles?.includes('AccountHolder' as any)) {
            jobCreatorRoleName = UserRoles.AccountHolder;
          } else if (parsedAuthInfo?.roles?.includes('Admin' as any)) {
            jobCreatorRoleName = UserRoles.Admin;
          }
        }

        if (isEmpty(jobCreatorRoleName) && !isEmpty(parsedAuthInfo) && !isEmpty(parsedAuthInfo?.roles)) {
          // If user role name neither AccountHolder nor Admin
          jobCreatorRoleName = parsedAuthInfo?.roles?.[0] || '';
        }
      }

      const allOngoingInProgressJobsResponse =
        !isEmpty(jobCreatorRoleName) && !isEmpty(searchText)
          ? await WorkOrdersApiService.fetchAllWorkOrders({
              vendorId: customerId,
              pageNumber,
              searchText,
              jobCreatedBy: userId,
              jobCreatorRoleName,
            })
          : { data: [], metadata: [], errors: [] };
      setTotalCount(allOngoingInProgressJobsResponse?.metadata?.totalCount);
      return allOngoingInProgressJobsResponse;
    } else {
      let roles = [];
      if (!isEmpty(userId)) {
        const userRolesAndPermission = await OrganizationService.getUserRoleAndPermission(userId);

        roles = userRolesAndPermission?.data?.roles;
      }
      const allOngoingInProgressJobsResponse = await WorkOrdersApiService.fetchAllWorkOrders({
        customerId: customerId,
        pageNumber,
        searchText,
        jobCreatedBy: userId,
        jobCreatorRoleName: roles?.[0]?.roleName,
      });
      setTotalCount(allOngoingInProgressJobsResponse?.metadata?.totalCount);

      return allOngoingInProgressJobsResponse;
    }
  };

  const getCustomerIdBasedOnRole = (facilityCustomerId: string) => {
    let { customerId = '' } = getStoredCustomerDetails() || {};
    if (storedAuthDetails[CUSTOM_ROLE_KEY] === UserType.Vendor) {
      customerId = facilityCustomerId;
    }
    return customerId;
  };

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const fetchJobs = useCallback(async (facilityCustomerId: string, pageNumber?: number, searchText?: string) => {
    try {
      const customerId = getCustomerIdBasedOnRole(facilityCustomerId);
      const jobsResponse = await getJobsResponse(customerId as string, pageNumber, searchText);

      if (jobsResponse?.data && jobsResponse?.errors.length === 0) {
        return jobsResponse?.data.map((jobItem: JobWithDueDates) => {
          const isFlow2OrFlow3Job = isEmpty(jobItem?.parentWorkOrderId) && !isEmpty(jobItem?.subWorkOrders);
          return {
            startDate: jobItem.jobStartDate ?? jobItem.actualStartDate,
            stopDate: jobItem.jobEndDate ?? jobItem.actualEndDate,
            value:
              isFlow2OrFlow3Job && isDistrictManager && !location.state?.isParentJob
                ? jobItem?.subWorkOrders?.[0]?.jobId
                : jobItem?.jobId,
            facilityId: jobItem?.facilities?.[0]?.facilityId ?? '',
            jobId:
              isFlow2OrFlow3Job && isDistrictManager && !location.state?.isParentJob
                ? jobItem?.subWorkOrders?.[0]?.jobId
                : jobItem?.jobId ?? '',
            id:
              isFlow2OrFlow3Job && isDistrictManager && !location.state?.isParentJob
                ? jobItem?.subWorkOrders?.[0]?.jobId
                : jobItem?.jobId,
            label: jobItem.jobName ?? jobItem.name,
            facilityName: jobItem?.facilities?.[0]?.facilityName || '',
            facilities: {
              id: jobItem?.facilities?.[0]?.facilityId ?? '',
              facilityId: jobItem?.facilities?.[0]?.facilityId ?? '',
              jobId: jobItem?.jobId ?? '',
              location: jobItem?.facilities?.[0]?.facilityAddress || '',
              buildingName: jobItem?.facilities?.[0]?.facilityName || '',
              Jobname: jobItem.jobName ?? jobItem.name,
            },
            location: jobItem?.facilities?.[0]?.facilityAddress || '',

            vendorId: jobItem.jobBids?.[0]?.vendorId ?? jobItem.vendorId,
            workOrderId: jobItem?.workOrderId,
            customerId: jobItem?.customerId,
            status: jobItem?.status,
            isFlow2OrFlow3Job: isFlow2OrFlow3Job,
            supervisorName: jobItem?.supervisorName,
            supervisorEmail: jobItem?.supervisorEmail,
            actualEndDate: jobItem?.actualEndDate,
            totalAmount: jobItem?.totalAmount,
            workTicketNo: jobItem?.intId,
            woScheduleMasterId: jobItem?.shiftDetails?.[0]?.woScheduleMasterId,
            workTicketStatus: jobItem?.workTicketStatus,
          };
        });
      }
      if (jobsResponse?.errors.length) {
        console.error('Error', jobsResponse?.errors);
        return [];
      }
      return [];
    } catch (e) {
      console.error('Error', e);
      return [];
    }
  }, []);

  const fetchAllJobs = useCallback(
    async (customerId: string, pageNumber?: number, searchText?: string) => {
      const [allJobs] = await Promise.all([fetchJobs(customerId, pageNumber, searchText)]);

      if (allJobs?.length) {
        return [...allJobs];
      }
      return [];
    },
    [fetchJobs]
  );

  const fetchVendorDetails = useCallback(async (vendorId: string) => {
    if (vendorId) {
      setVendorLoading(true);
      const vendorResponse = await FacilityApiService.vendorDetails(vendorId, isMock);
      const resultLogo = await Storage.get(`vendor/${vendorId}/company-logos/${vendorId}`, { level: 'public' });
      if (vendorResponse.errors?.length) {
        setIsVendorQueryError(true);
        console.error('vendor query: ', vendorResponse);
      } else {
        const { vendorCompanyName, phone, email } = vendorResponse.data;
        const { addressLine1, addressLine2, addressLine3 } = getVendorAddress(vendorResponse?.data?.vendorAddress?.[0]);
        setVendorDetails({
          vendorPersonName: vendorCompanyName ?? '',
          vendorPersonIcon: resultLogo ?? '',
          vendorName: `${addressLine1 ?? ''} ${addressLine2 ?? ''} ${addressLine3 ?? ''}`,
          vendorLocation: '',
          VendorPersonMailId: email ?? '',
          vendorContactNumber: phone ?? '',
        });
        setIsVendorQueryError(false);
      }
    }
    setVendorLoading(false);
  }, []);

  const getVendorAddress = (vendorAddress: any) => {
    const { addressLine1, addressLine2, addressLine3 } = vendorAddress?.length
      ? vendorAddress
      : { addressLine3: '', addressLine1: '', addressLine2: '' };
    return { addressLine1, addressLine2, addressLine3 };
  };

  const fetchJobsForSelectedFacility = useCallback(
    // eslint-disable-next-line sonarjs/cognitive-complexity
    async (customerId: string, pageNumber?: number, searchText?: string) => {
      setJobsLoading(true);
      const jobs: any = await fetchAllJobs(customerId, pageNumber, searchText);

      let selectedJobId: string = '';

      if (isEmpty(searchString) && isEmpty(searchText) && isFirstLoad.current) {
        if (location.state && location.state.jobId) {
          selectedJobId = location.state.jobId;
        } else if (location.state?.facilityItem?.workOrderId) {
          const matchedJob = jobs.find(
            (job: { workOrderId: any }) => job.workOrderId === location.state?.facilityItem?.workOrderId
          );
          selectedJobId = matchedJob ? matchedJob.jobId : null;
        } else {
          selectedJobId = '';
        }
        setSelectedJob(selectedJobId);
        const selectedJobVal = jobs.find((jobItem: any) => jobItem.jobId === selectedJobId);

        const supervisorName = selectedJobVal?.supervisorName;
        const supervisorEmail = selectedJobVal?.supervisorEmail;
        setSupervisorName(supervisorName);
        setSupervisorEmail(supervisorEmail);
        setParentWoScheduleMasterId(selectedJobVal?.woScheduleMasterId);
        fetchSelectedJobData(selectedJobVal);
        setOriginalJobName(selectedJobVal?.label);
        setOriginalSelectedJobDetails(selectedJobVal);
        setOriginalFacilityId(selectedJobVal?.facilityId);
      }

      if (jobs && jobs.length > 0) {
        setScreenLoading(true);
        setAllJobs(jobs);
        setScreenLoading(false);
      } else {
        setAllJobs([]);
      }
      setJobsLoading(false);
    },
    []
  );

  const fetchFacilityAccess = async () => {
    try {
      setIsFacilityAccessLoader(true);
      const { customerId = '' } = getStoredCustomerDetails() || {};
      const facilityAccess = await CustomerApiService.getFacilityAccess(customerId, facilityId);

      const userDetailsInput: UserDetailsInput = {
        userIds: [facilityAccess?.data?.[0]?.accessContactPerson],
      };
      const usersResponse = await OrganizationService.getUsersDetails(userDetailsInput);

      setFacilityAccessDetails({
        accessCode: facilityAccess?.data?.[0]?.accessCode,
        otherInformation: facilityAccess?.data?.[0]?.otherInformation,
        username: usersResponse?.data?.[0]?.username,
        phoneNumber: usersResponse?.data?.[0]?.phoneNumber,
      });
    } catch (error) {
      console.error(t('dashboard:errorFetchingFacilityAccessData'), error);
    } finally {
      setIsFacilityAccessLoader(false);
    }
  };

  useEffect(() => {
    if (facilityId) {
      fetchFacilityAccess();
    }
  }, [facilityId]);

  useEffect(() => {
    if (selectedJob) {
      const selectedJobVal = allJobs.find((jobItem: any) => jobItem.jobId === selectedJob);
      if (!isEmpty(selectedJobVal)) {
        setSelectedJobDetails(selectedJobVal);
        setJobName(selectedJobVal?.label);
        setFacilityId(selectedJobVal?.facilityId);
        fetchSelectedJobData(selectedJobVal);
      }
    }
  }, [selectedJob]);
  const renderPerformanceMatrix = () => {
    return (
      <>
        <PerformanceMetrics>{t('dashboard:performanceMetrics')}</PerformanceMetrics>

        {performanceLoader ? (
          <Loader>
            <CircularProgress size={30} sx={{ color: theme.palette.primary.dark }} />
          </Loader>
        ) : performanceMetrics.length ? (
          <>
            {performanceMetrics.map((dataItem, index) => {
              const progress = dataItem?.totalTasks !== 0 ? (dataItem.completed / dataItem.totalTasks) * 100 : 0;
              return (
                <PerformanceMetricsWrapper key={index}>
                  {renderDateContainer(dataItem, index)}
                  {/* <DateContainer>
                    <div>
                      <img src={Icons.Calendar} alt={t('altTexts:calendar')} />
                      <DateWrapper>
                        {index === 0 ? (
                          dataItem.title
                        ) : (
                          <>
                            {dataItem.title}
                            <DaysRange>{dataItem.days}</DaysRange>
                          </>
                        )}
                      </DateWrapper>
                    </div>
                    {index === 0 ? (
                      <SimpleDatePicker
                        isResponsive={false}
                        defaultValue={selectedDate}
                        onChange={(date) => setSelectedDate(moment(date).toDate())}
                        CustomComponent={
                          <ChangeDate>
                            {t('dashboard:changeDate')} <img src={Icons.EditBlackIcon} alt={t('altTexts:modify')} />
                          </ChangeDate>
                        }
                      />
                    ) : null}
                  </DateContainer> */}
                  <AssociateDetails>
                    <TaskStatusWrapper>
                      <StatusWrapper>
                        <h5>
                          {t('dashboard:completed')}: <strong>{dataItem.completed}</strong>
                        </h5>
                        <h5>
                          {t('dashboard:incomplete')}: <strong>{dataItem.totalTasks - dataItem.completed}</strong>
                        </h5>
                      </StatusWrapper>
                      <ProgressBarWrapper>
                        <h5>0 tasks</h5>
                        <ProgressBarContainer>
                          <ProgressBar progressPercent={progress}></ProgressBar>
                        </ProgressBarContainer>
                        <h5>{dataItem.totalTasks} tasks</h5>
                      </ProgressBarWrapper>
                    </TaskStatusWrapper>
                    {dataItem.punchInTime && dataItem.punchOutTime ? (
                      <AssociateTimingWrapper>
                        <div>
                          <ClockWrapper>
                            <img src={Icons.ClockIcon} alt={t('altTexts:time')} />
                          </ClockWrapper>
                          <AssociateDurationDetails>
                            <Timings>{`${dataItem.punchInTime} - ${dataItem.punchOutTime}`}</Timings>
                            <h5>{t('dashboard:associatesOnsite')}</h5>
                          </AssociateDurationDetails>
                        </div>
                      </AssociateTimingWrapper>
                    ) : null}
                  </AssociateDetails>
                  <CardWrapper>
                    <Card>
                      <div>
                        <PieChartScore score={dataItem.inspectionScore}></PieChartScore>
                        <TitleStatus>{`${ParseFloat(dataItem?.inspectionScore?.toString(), 2)}%`}</TitleStatus>
                      </div>
                      <TitleText>{t('dashboard:inspectionScore')}</TitleText>
                    </Card>
                    {/* <Card>
                          <div>
                            <img src={Icons.StarRatingFilledIcon} alt={t('altTexts:startRating')} />
                            <TitleStatus>{dataItem.satisfactionScore}</TitleStatus>
                          </div>
                          <TitleText>Satisfaction score</TitleText>
                        </Card> */}
                    <IssuesRaised to="/jobs-issues-raised">
                      <div>
                        <img src={Icons.TicketIcon} alt={t('altTexts:ticket')} />
                        <TitleStatus>{dataItem.issuesRaised}</TitleStatus>
                      </div>
                      <div>
                        <TitleText>{t('dasboard:issuesRaised')}</TitleText>
                        <img src={Icons.ChevronRightIcon} alt={t('altTexts:rightArrow')} />
                      </div>
                    </IssuesRaised>
                    <OneTimeJobs state={{ isOneTimeJob: true }} to={{ pathname: '/jobs-view/onetime' }}>
                      <div>
                        <img src={Icons.JobIcon} alt={t('altTexts:jobs')} />
                        <TitleStatus>{dataItem.oneTimeJobs}</TitleStatus>
                      </div>
                      <div>
                        <TitleText>{t('dasboard:oneTimeJobs')}</TitleText>
                        <img src={Icons.ChevronRightIcon} alt={t('altTexts:rightArrow')} />
                      </div>
                    </OneTimeJobs>
                  </CardWrapper>
                </PerformanceMetricsWrapper>
              );
            })}
          </>
        ) : isAnalyticsAPIError ? (
          <ErrorMessage />
        ) : (
          <NoDataFound />
        )}
      </>
    );
  };

  const renderDateContainer = (dataItem: any, index: number) => {
    return (
      <DateContainer>
        <div>
          <img src={Icons.Calendar} alt={t('altTexts:calendar')} />
          <DateWrapper>
            {index === 0 ? (
              dataItem.title
            ) : (
              <>
                {dataItem.title}
                <DaysRange>{dataItem.days}</DaysRange>
              </>
            )}
          </DateWrapper>
        </div>
        {index === 0 ? (
          <SimpleDatePicker
            isResponsive={false}
            defaultValue={selectedDate}
            onChange={(date) => setSelectedDate(moment(date as MomentInput).toDate())}
            CustomComponent={
              <ChangeDate>
                {t('dashboard:changeDate')} <img src={Icons.EditBlackIcon} alt={t('altTexts:modify')} />
              </ChangeDate>
            }
          />
        ) : null}
      </DateContainer>
    );
  };

  const getFacilityAddress = () => {
    return `${addressLine1 ?? ''} ${addressLine2 ?? ''}, ${
      addressLine3 !== 'undefined' && addressLine3 !== null ? addressLine3 : ''
    } ${city ?? ''}, ${states[stateCode] ?? ''} ${county ?? ''}`;
  };

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const showConversationTitle = useCallback((jobDetails: any) => {
    if (role === UserType.Customer) {
      return t('conversations:chatWithVendor');
    } else if (role === UserType.Vendor) {
      if (isDistrictManager) {
        if (isDistrictManager && jobDetails?.isSubVendorInSidePeazy) {
          return;
        }
        return !jobDetails?.isSelfPerformJob &&
          jobDetails?.estimationType !== JobDetails.EstimateOutsidePeazy &&
          jobDetails?.executionType !== JobDetails.SubVendor
          ? t('conversations:chatWithCustomer')
          : null;
      } else {
        return jobDetails?.isSubVendorInSidePeazy ||
          jobDetails?.isSubVendorPerformingActionForRegularJobs ||
          jobDetails?.primeVendorForRecurringJobs
          ? t('conversations:chatWithDM')
          : !jobDetails?.isSelfPerformJob &&
              jobDetails?.estimationType !== JobDetails.EstimateOutsidePeazy &&
              jobDetails?.executionType !== JobDetails.SubVendor
            ? t('conversations:chatWithCustomer')
            : null;
      }
    }
    return '';
  }, []);

  const fetchJobSummary = async (customerId: string, jobId: string) => {
    return await JobCreateApiService.jobSummaryV2(customerId, jobId);
  };

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const setDataForChat = useCallback(async (jobDetails: any) => {
    try {
      setIsConversationsDataLoading(true);
      const { customerId = '' } = getStoredCustomerDetails() || {};
      const getUserNameForCustomerAndDm = await OrganizationService.getUserNames({ jobId: jobDetails?.jobId });
      const isSubVendorPerformingActionForRegularJobs =
        (jobDetails?.jobType === JobType.Recurring || jobDetails?.jobType === JobType.Periodic) &&
        !isEmpty(jobDetails?.parentWorkOrderId ?? '') &&
        !isEmpty(jobDetails?.jobCustodian);

      const primeVendorForRecurringJobs =
        (jobDetails?.jobType === JobType.Recurring || jobDetails?.jobType === JobType.Periodic) &&
        isEmpty(jobDetails?.parentWorkOrderId ?? '') &&
        !isEmpty(jobDetails?.jobCustodian);

      const isOTJFlow =
        !isEmpty(jobDetails?.jobCustodian) &&
        (jobDetails?.jobType === JobType.OneTime || jobDetails?.jobType === JobType.Emergency);

      const isSubVendorInSidePeazy =
        isOTJFlow &&
        jobDetails?.executionType === JobDetails.SubVendor &&
        jobDetails?.estimationType === JobExecution.EstimateWithinPeazy;

      const isSubVendorOutSidePeazy =
        isOTJFlow &&
        jobDetails?.executionType === JobDetails.SubVendor &&
        jobDetails?.estimationType === JobExecution.EstimateOutsidePeazy;

      let contextData: IConversationContext = {} as IConversationContext;
      if (role === UserType.Vendor) {
        if (!isDistrictManager && (isSubVendorOutSidePeazy || isSubVendorInSidePeazy)) {
          if (isSubVendorOutSidePeazy) {
            return;
          }

          contextData = {
            userType: UserType.Vendor,
            userId1: loggedInUserInternalId ?? '',
            userId1Name: getUserNameForCustomerAndDm?.data?.[0]?.subVendorName ?? '',
            userId2: getUserNameForCustomerAndDm?.data?.[0]?.jobCreatorUserId ?? '',
            userId2Name: getUserNameForCustomerAndDm?.data?.[0]?.vendorName ?? '',
            userId2UserName: getUserNameForCustomerAndDm?.data?.[0]?.jobCreatorUserName ?? '',
            userId1UserName: loggedInUserName ?? '',
            jobId: jobDetails?.jobId,

            userAndJobDetails: {
              jobName: jobDetails?.workOrderName ?? jobDetails?.jobName ?? '',
              vendorId: loggedInUserInternalId ?? '',
              jobId: jobDetails?.jobId,
              vendorUserName: loggedInUserName ?? '',
              vendorCompanyName: getUserNameForCustomerAndDm?.data?.[0]?.subVendorName ?? '',
              userType: UserType.Vendor,
              facilityName: '',
              conversationGroupName: '',
              associateId: '',
              associateName: '',
              customerOrDmSubVendorId: getUserNameForCustomerAndDm?.data?.[0]?.jobCreatorUserId ?? '',
              customerOrDmOrSubVendorUserName: getUserNameForCustomerAndDm?.data?.[0]?.jobCreatorUserName ?? '',
              customerOrDmOrSubVendorCompanyName: getUserNameForCustomerAndDm?.data?.[0]?.vendorName ?? '',
            },
            primaryContactName: t('common:chatWithDM'),
          };
        } else if (isDistrictManager && (isSubVendorOutSidePeazy || isSubVendorInSidePeazy)) {
          if (isSubVendorOutSidePeazy) {
            return;
          }

          const getUserNameForCustomerAndDm = await OrganizationService.getUserNames({
            jobId: jobDetails?.subJobs?.[0]?.jobId ?? '',
          });

          contextData = {
            userType: UserType.Vendor,
            userId1: loggedInUserInternalId ?? '',
            userId1Name: getUserNameForCustomerAndDm?.data?.[0]?.vendorName ?? '',
            userId1UserName: loggedInUserName ?? '',
            userId2: getUserNameForCustomerAndDm?.data?.[0]?.subVendorUserId ?? '',
            userId2Name: getUserNameForCustomerAndDm?.data?.[0]?.subVendorName ?? '',
            userId2UserName: getUserNameForCustomerAndDm?.data?.[0]?.subVendorUserName ?? '',
            jobId: jobDetails?.subJobs?.[0]?.jobId,

            userAndJobDetails: {
              jobName: jobDetails?.workOrderName ?? jobDetails?.jobName ?? '',
              vendorId: loggedInUserInternalId ?? '',
              jobId: jobDetails?.subJobs?.[0]?.jobId,
              vendorUserName: loggedInUserName ?? '',
              vendorCompanyName: getUserNameForCustomerAndDm?.data?.[0]?.vendorName ?? '',
              userType: UserType.Vendor,
              facilityName: '',
              conversationGroupName: '',
              associateId: '',
              associateName: '',
              customerOrDmSubVendorId: getUserNameForCustomerAndDm?.data?.[0]?.subVendorUserId ?? '',
              customerOrDmOrSubVendorUserName: getUserNameForCustomerAndDm?.data?.[0]?.subVendorUserName ?? '',
              customerOrDmOrSubVendorCompanyName: getUserNameForCustomerAndDm?.data?.[0]?.subVendorName ?? '',
            },
            primaryContactName: t('common:chatWithSubVendor'),
          };
        } else if (isSubVendorPerformingActionForRegularJobs || primeVendorForRecurringJobs) {
          const getDataForRecurringJobs = await OrganizationService.getUserNames({
            jobId: jobDetails?.jobId ?? '',
          });

          contextData = {
            userType: UserType.Vendor,
            userId1: loggedInUserInternalId ?? '',
            userId1Name: isDistrictManager
              ? getDataForRecurringJobs?.data?.[0]?.vendorName ?? ''
              : getDataForRecurringJobs?.data?.[0]?.subVendorName ?? '',
            userId1UserName: loggedInUserName ?? '',
            userId2: isDistrictManager
              ? getDataForRecurringJobs?.data?.[0]?.jobCreatorUserId ?? ''
              : getDataForRecurringJobs?.data?.[0]?.vendorUserId ?? '',
            userId2Name: isDistrictManager
              ? getDataForRecurringJobs?.data?.[0]?.customerName ?? ''
              : getDataForRecurringJobs?.data?.[0]?.vendorName ?? '',
            userId2UserName: isDistrictManager
              ? getUserNameForCustomerAndDm?.data?.[0]?.jobCreatorUserName ?? ''
              : getDataForRecurringJobs?.data?.[0]?.vendorUsername ?? '',
            jobId: jobDetails?.jobId,

            userAndJobDetails: {
              jobName: jobDetails?.workOrderName ?? jobDetails?.jobName ?? '',
              vendorId: loggedInUserInternalId ?? '',
              jobId: jobDetails?.jobId,
              vendorUserName: loggedInUserName ?? '',
              vendorCompanyName: isDistrictManager
                ? getDataForRecurringJobs?.data?.[0]?.vendorName ?? ''
                : getDataForRecurringJobs?.data?.[0]?.subVendorName ?? '',
              userType: UserType.Vendor,
              facilityName: '',
              conversationGroupName: '',
              associateId: '',
              associateName: '',
              customerOrDmSubVendorId: isDistrictManager
                ? getDataForRecurringJobs?.data?.[0]?.jobCreatorUserId ?? ''
                : getDataForRecurringJobs?.data?.[0]?.vendorUserId ?? '',
              customerOrDmOrSubVendorUserName: isDistrictManager
                ? getUserNameForCustomerAndDm?.data?.[0]?.jobCreatorUserName ?? ''
                : getDataForRecurringJobs?.data?.[0]?.vendorUsername ?? '',
              customerOrDmOrSubVendorCompanyName: isDistrictManager
                ? getDataForRecurringJobs?.data?.[0]?.customerName ?? ''
                : getDataForRecurringJobs?.data?.[0]?.vendorName ?? '',
            },
            primaryContactName: t('common:chatWithSubVendor'),
          };
        } else {
          if (jobDetails?.isSelfPerformJob) return;

          const customerDetailsResponse = await CustomerApiService.getCustomerDetailsResponse(
            jobDetails?.customerId as string
          );

          contextData = {
            userType: UserType.Vendor,
            userId1: loggedInUserInternalId ?? '',
            userId1Name: getUserNameForCustomerAndDm?.data?.[0]?.vendorName ?? '',
            userId1UserName: loggedInUserName ?? '',
            userId2: getUserNameForCustomerAndDm?.data?.[0]?.jobCreatorUserId ?? '',
            userId2Name: customerDetailsResponse?.data?.customer?.doingBusinessAs ?? '',
            userId2UserName: getUserNameForCustomerAndDm?.data?.[0]?.jobCreatorUserName ?? '',
            jobId: jobDetails?.jobId ?? '',

            userAndJobDetails: {
              jobName: jobDetails?.workOrderName ?? jobDetails?.jobName ?? '',
              vendorId: loggedInUserInternalId ?? '',
              jobId: jobDetails?.jobId ?? '',
              vendorUserName: loggedInUserName ?? '',
              vendorCompanyName: getUserNameForCustomerAndDm?.data?.[0]?.vendorName ?? '',
              userType: UserType.Vendor,
              facilityName: '',
              conversationGroupName: '',
              associateId: '',
              associateName: '',
              customerOrDmSubVendorId: getUserNameForCustomerAndDm?.data?.[0]?.jobCreatorUserId ?? '',
              customerOrDmOrSubVendorUserName: getUserNameForCustomerAndDm?.data?.[0]?.jobCreatorUserName ?? '',
              customerOrDmOrSubVendorCompanyName: customerDetailsResponse?.data?.customer?.doingBusinessAs ?? '',
            },
            primaryContactName: t('common:chatWithCustomer'),
          };
        }
      } else {
        const jobSummaryResponse = await fetchJobSummary(customerId, jobDetails?.jobId);
        if (
          role === UserType.Customer &&
          !isEmpty(jobSummaryResponse) &&
          (jobSummaryResponse?.data?.dataStatus === JobStatus.Awarded ||
            jobSummaryResponse?.data?.dataStatus === JobStatus.Completed ||
            jobSummaryResponse?.data?.dataStatus === JobStatus.InProgress)
        ) {
          const jobBidsResponse = await JobCreateApiService.getJobBids(jobDetails?.jobId);

          const jobBids = jobBidsResponse?.data?.map((bid: { dataStatus: any; vendorId: any; vendorName: any }) => ({
            dataStatus: bid?.dataStatus,
            vendorId: bid?.vendorId,
            vendorName: bid?.vendorName,
          }));

          const vendorUserName = await OrganizationService.getUserNames({
            jobId: jobDetails?.jobId ?? '',
          });
          const vendorId = jobDetails?.vendorId;
          const awardedBids =
            jobSummaryResponse?.data?.dataStatus === JobStatus.Completed
              ? {
                  vendorId,
                  vendorUserName: vendorUserName?.data?.[0]?.vendorUserName ?? '',
                  vendorName: vendorUserName?.data?.[0]?.vendorName ?? '',
                }
              : jobBids?.find((item: { dataStatus: BidStatus }) => item?.dataStatus === BidStatus.C);

          contextData = {
            userId1: vendorUserName?.data?.[0]?.vendorUserId ?? '',
            userId1Name: awardedBids?.vendorName ?? '',
            userId2: loggedInUserInternalId ?? '',
            userId2Name: jobSummaryResponse?.data?.customerName ?? '',
            userId2UserName: loggedInUserName ?? '',
            userId1UserName: vendorUserName?.data?.[0]?.vendorUserName ?? '',
            jobId: jobSummaryResponse?.data?.jobId ?? '',
            userType: UserType.Customer,

            userAndJobDetails: {
              jobName: jobSummaryResponse?.data?.jobName ?? '',
              vendorId: vendorUserName?.data?.[0]?.vendorUserId ?? '',
              jobId: jobSummaryResponse?.data?.jobId ?? '',
              vendorUserName: vendorUserName?.data?.[0]?.vendorUserName ?? '',
              vendorCompanyName: awardedBids?.vendorName ?? '',
              userType: UserType.Customer,
              facilityName: '',
              conversationGroupName: '',
              associateId: '',
              associateName: '',
              customerOrDmSubVendorId: loggedInUserInternalId ?? '',
              customerOrDmOrSubVendorUserName: loggedInUserName ?? '',
              customerOrDmOrSubVendorCompanyName: jobSummaryResponse?.data?.customerName ?? '',
            },
            primaryContactName: t('common:chatWithVendor'),
          };
        }
      }

      handleConversationIconClick(jobDetails, contextData);
      setIsConversationsDataLoading(false);
    } catch (error) {
      console.error('Error fetching vendor details or setting conversation context:', error);
    }
  }, []);

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const setDataForSubVendors = useCallback(async (jobDetails: any) => {
    try {
      setIsConversationsDataLoading(true);

      const isOTJFlow =
        !isEmpty(jobDetails?.jobCustodian) &&
        (jobDetails?.jobType === JobType.OneTime || jobDetails?.jobType === JobType.Emergency);

      const isSubVendorInSidePeazy =
        isOTJFlow &&
        jobDetails?.executionType === JobDetails.SubVendor &&
        jobDetails?.estimationType === JobExecution.EstimateWithinPeazy;

      const isSubVendorOutSidePeazy =
        isOTJFlow &&
        jobDetails?.executionType === JobDetails.SubVendor &&
        jobDetails?.estimationType === JobExecution.EstimateOutsidePeazy;

      let contextData: IConversationContext = {} as IConversationContext;

      if (role === UserType.Vendor && isDistrictManager && (isSubVendorOutSidePeazy || isSubVendorInSidePeazy)) {
        if (isSubVendorOutSidePeazy) {
          return;
        }

        const getUserNameForCustomerAndDm = await OrganizationService.getUserNames({
          jobId: jobDetails?.subJobs?.[0]?.jobId ?? '',
        });

        contextData = {
          userType: UserType.Vendor,
          userId1: loggedInUserInternalId ?? '',
          userId1Name: getUserNameForCustomerAndDm?.data?.[0]?.vendorName ?? '',
          userId1UserName: loggedInUserName ?? '',
          userId2: getUserNameForCustomerAndDm?.data?.[0]?.subVendorUserId ?? '',
          userId2Name: getUserNameForCustomerAndDm?.data?.[0]?.subVendorName ?? '',
          userId2UserName: getUserNameForCustomerAndDm?.data?.[0]?.subVendorUserName ?? '',
          jobId: jobDetails?.subJobs?.[0]?.jobId,

          userAndJobDetails: {
            jobName: jobDetails?.workOrderName ?? jobDetails?.jobName ?? '',
            vendorId: loggedInUserInternalId ?? '',
            jobId: jobDetails?.subJobs?.[0]?.jobId,
            vendorUserName: loggedInUserName ?? '',
            vendorCompanyName: getUserNameForCustomerAndDm?.data?.[0]?.vendorName ?? '',
            userType: UserType.Vendor,
            facilityName: '',
            conversationGroupName: '',
            associateId: '',
            associateName: '',
            customerOrDmSubVendorId: getUserNameForCustomerAndDm?.data?.[0]?.subVendorUserId ?? '',
            customerOrDmOrSubVendorUserName: getUserNameForCustomerAndDm?.data?.[0]?.subVendorUserName ?? '',
            customerOrDmOrSubVendorCompanyName: getUserNameForCustomerAndDm?.data?.[0]?.subVendorName ?? '',
          },
          primaryContactName: t('common:chatWithSubVendor'),
        };
      }

      handleConversationIconClick(jobDetails, contextData);
      setIsConversationsDataLoading(false);
    } catch (error) {
      console.error('Error fetching vendor details or setting conversation context:', error);
    }
  }, []);

  const handleConversationIconClick = (jobDetails: any, contextData: IConversationContext) => {
    const queryParams = new URLSearchParams({
      jobId: jobDetails?.jobId,
    });

    navigate(`/post-job-award-conversations?${queryParams.toString()}`, {
      state: {
        ...jobDetails,
        conversationContext: contextData,
      },
    });
  };

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const setJobData = useCallback(
    // eslint-disable-next-line sonarjs/cognitive-complexity
    async (selectedJob?: string) => {
      const { customerId = '' } = getStoredCustomerDetails() || {};
      if (selectedJob) {
        const jobSummaryResponse = await fetchJobSummary(customerId, selectedJob);

        if (!isEmpty(jobSummaryResponse?.data)) {
          setJobSummaryResponseData(jobSummaryResponse?.data);
        } else if (isEmpty(jobSummaryResponse?.data) && !isEmpty(jobSummaryResponse.errors)) {
          console.error('jobSummaryV2 queries: ', jobSummaryResponse.errors);
        }
        const modifiedJob: any = { ...jobSummaryResponse?.data };
        let allWorkOrderResponseData;

        if (!isFirstJobsDataLoad.current) {
          const workOrderResponse = await WorkOrdersApiService.fetchAllWorkOrders({
            jobId: selectedJob ?? location.state?.jobId,
          });
          allWorkOrderResponseData = workOrderResponse?.data;
          setWorkOrderResponseData(allWorkOrderResponseData);
        } else {
          allWorkOrderResponseData = workOrderResponseData;
        }
        const isOtjJob =
          !!jobSummaryResponse?.data?.jobCustodian &&
          jobSummaryResponse?.data?.jobType !== JobType.Recurring &&
          jobSummaryResponse?.data?.jobType !== JobType.Periodic;
        modifiedJob.customerId = jobSummaryResponse?.data?.customerId;

        const executionType = isOtjJob
          ? jobSummaryResponse?.data?.jobVendors?.[0]?.mdVendorType === JobExecution.SubVendor ||
            jobSummaryResponse?.data?.jobVendors?.[0]?.mdVendorType === JobExecution.All
            ? JobDetails.SubVendor
            : JobDetails.Self
          : '';
        modifiedJob.executionType = executionType;
        const estimationType = isOtjJob
          ? jobSummaryResponse?.data?.jobVendors?.[0]?.mdVendorType === JobExecution.All
            ? JobDetails.EstimateWithinPeazy
            : JobDetails.EstimateOutsidePeazy
          : '';
        modifiedJob.estimationType = estimationType;
        modifiedJob.jobName = jobSummaryResponse?.data?.jobName;
        modifiedJob.jobCustodian = jobSummaryResponse?.data?.jobCustodian;
        modifiedJob.subJobs = jobSummaryResponse?.data?.subJobs;
        modifiedJob.parentWorkOrderId = allWorkOrderResponseData?.[0]?.parentWorkOrderId;
        modifiedJob.vendorId = allWorkOrderResponseData?.[0]?.vendorId;
        modifiedJob.isSelfPerformJob =
          jobSummaryResponse?.data?.jobVendors?.[0]?.mdVendorType === VendorType.SelfPerform;

        modifiedJob.primeVendorForRecurringJobs =
          (jobSummaryResponse?.data?.jobType === JobType.Recurring ||
            jobSummaryResponse?.data?.jobType === JobType.Periodic) &&
          isEmpty(allWorkOrderResponseData?.[0]?.parentWorkOrderId ?? '') &&
          !isEmpty(jobSummaryResponse?.data?.jobCustodian ?? allWorkOrderResponseData?.[0]?.jobCustodian);

        modifiedJob.isSubVendorPerformingActionForRegularJobs =
          (jobSummaryResponse?.data?.jobType === JobType.Recurring ||
            jobSummaryResponse?.data?.jobType === JobType.Periodic) &&
          !isEmpty(allWorkOrderResponseData?.[0]?.parentWorkOrderId ?? '') &&
          !isEmpty(jobSummaryResponse?.data?.jobCustodian ?? allWorkOrderResponseData?.[0]?.jobCustodian);
        modifiedJob.isSubVendorInSidePeazy =
          isOtjJob && executionType === JobDetails.SubVendor && estimationType === JobDetails.EstimateWithinPeazy;

        setJobDetails(modifiedJob);
        isFirstJobsDataLoad.current = false;
        return modifiedJob;
      }
    },
    [selectedJob]
  );

  useEffect(() => {
    if (selectedJob) {
      setJobData(selectedJob);
    } else if (isEmpty(selectedJob) && !isFirstLoad.current) {
      setJobData(location.state?.jobId);
    }
  }, [selectedJob]);

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const renderOverviewTab = () => {
    return (
      <>
        <FacilityInfoWrapper>
          <FacilityDetails>
            <IconWrapper>
              <PersonIcon>
                <img src={FacilityIcon} alt={t('altTexts:facility')} width={56} height={56} />
              </PersonIcon>
              {isFacilityAccessLoader ? (
                <LoaderWrapper>
                  <CircularProgress size={30} sx={{ color: theme.palette.primary.dark }} />
                </LoaderWrapper>
              ) : (
                <NameWrapper>
                  <PersonName>{buildingName}</PersonName>
                  <Name>{getFacilityAddress()}</Name>
                  {facilityAccessDetails?.accessCode && (
                    <AccessPinWrapper>
                      <AccessPin>{t('altTexts:accessPin')}</AccessPin>
                      <AccessPinValue>{facilityAccessDetails?.accessCode}</AccessPinValue>
                    </AccessPinWrapper>
                  )}
                </NameWrapper>
              )}
            </IconWrapper>
            <FacilityDurationWrapper>
              <FacilityJobName>{selectedJobDetails?.jobName}</FacilityJobName>
              <FacilityDuration>
                {isDateLoading ? (
                  <CircularProgress size={24} sx={{ color: theme.palette.primary.dark }} />
                ) : (
                  `${t('dashboard:duration')} ${formatDate(startDate as string | undefined, 'MMM D, YYYY')} to ${formatDate(stopDate as string | undefined, 'MMM D, YYYY')}`
                )}
              </FacilityDuration>
              {status && (
                <FacilityDuration>
                  {t('dashboard:status')} : {WorkOrderStatus[status] ?? '-'}
                </FacilityDuration>
              )}
              {jobType && (
                <FacilityDuration>
                  {t('dashboard:jobType')} : {jobType ?? '-'}
                </FacilityDuration>
              )}
            </FacilityDurationWrapper>
          </FacilityDetails>
          <ShiftAndChatContainer>
            {isServicesLoading ? (
              <ProgressLoader>
                <CircularProgress size={30} sx={{ color: theme.palette.primary.dark }} />
              </ProgressLoader>
            ) : (
              <>{renderFacilityShiftDetails()}</>
            )}

            <ChatButtonContainer hasPrimeVendor={!!jobDetails?.primeVendorForRecurringJobs}>
              {hasAccessToConversations() &&
              ((isDistrictManager && jobDetails?.isSubVendorInSidePeazy) ||
                (isDistrictManager && jobDetails?.primeVendorForRecurringJobs)) ? (
                <Box
                  sx={{
                    ...chatWithTeamMember,
                    opacity: isConversationsDataLoading ? 0.5 : 1,
                  }}
                  onClick={() => setDataForSubVendors(jobDetails)}>
                  {isConversationsDataLoading ? (
                    <CircularProgress
                      size={24}
                      sx={{
                        color: theme.palette.primary.dark,
                      }}
                    />
                  ) : (
                    <>
                      <img
                        src={Icons.GreenConversationIcon}
                        alt={t('altTexts:conversation')}
                        style={{ width: '1.5rem', height: '1.5rem', marginRight: '8px', background: 'white' }}
                      />
                      <ConversationName style={{ color: '#006B6B' }}>
                        {(isDistrictManager && jobDetails?.isSubVendorInSidePeazy) ||
                        jobDetails?.primeVendorForRecurringJobs
                          ? t('conversations:chatWithSubVendors')
                          : ''}
                      </ConversationName>
                    </>
                  )}
                </Box>
              ) : null}
              {hasAccessToConversations() && showConversationTitle(jobDetails) ? (
                <Box
                  sx={{
                    ...chatWithTeamMember,
                    opacity: isConversationsDataLoading ? 0.5 : 1,
                  }}
                  onClick={() => setDataForChat(jobDetails)}>
                  {isConversationsDataLoading ? (
                    <CircularProgress
                      size={24}
                      sx={{
                        color: theme.palette.primary.dark,
                      }}
                    />
                  ) : (
                    <>
                      <img
                        src={Icons.GreenConversationIcon}
                        alt={t('altTexts:conversation')}
                        style={{ width: '1.5rem', height: '1.5rem', marginRight: '8px', background: 'white' }}
                      />
                      <ConversationName style={{ color: '#006B6B' }}>
                        {showConversationTitle ? showConversationTitle(jobDetails) : ''}
                      </ConversationName>
                    </>
                  )}
                </Box>
              ) : null}
            </ChatButtonContainer>
          </ShiftAndChatContainer>
        </FacilityInfoWrapper>
        {(isDistrictManager && location?.state?.executionType !== 'self') ||
        (role as UserType) === UserType.Customer ? (
          <>
            {!vendorLoading && vendorDetails ? (
              getVendorDetailsComp(vendorDetails)
            ) : (
              <NoDataWrapper>
                {vendorLoading ? (
                  <CircularProgress size={30} sx={{ color: theme.palette.primary.dark }} />
                ) : isVendorQueryError ? (
                  <ErrorText />
                ) : (
                  `${t('dashboard:noVendorFound')}`
                )}
              </NoDataWrapper>
            )}
          </>
        ) : null}
        <>
          {/* eslint-disable-next-line sonarjs/no-redundant-boolean */}
          {false && renderPerformanceMatrix()}
        </>
      </>
    );
  };

  const renderFacilityShiftDetails = () => {
    return (
      <FacilityShiftDetails>
        {Array.isArray(frequencies) && frequencies.length ? (
          frequencies.map((data: any, index) => {
            return (
              <Box>
                <FacilityShiftList key={index}>
                  {((jobType as JobType) === JobType.Recurring || (jobType as JobType) === JobType.Periodic) && (
                    <>
                      <ShiftTime>{data.shiftTiming}</ShiftTime>
                      <ShiftType>
                        {renderImg(data.shiftType as ShiftTypes)}
                        {data.shiftType}
                      </ShiftType>
                      {data?.schedulePattern && <ShiftTime>{data.schedulePattern}</ShiftTime>}
                    </>
                  )}
                </FacilityShiftList>
                <JobDetailsWrapper
                  sx={{
                    marginTop: data?.shiftTiming || data?.shiftType ? '1rem' : '0',
                  }}>
                  <Box sx={{ display: 'flex', gap: '0.5rem', whiteSpace: 'nowrap' }}>
                    <JobDetailLabel>{t('dashboard:serviceCategory')}</JobDetailLabel>
                    <FacilityDuration>
                      {data?.services.map((s: any) => s?.categoryName).join(', ') ?? EMPTY_VALUE}
                    </FacilityDuration>
                  </Box>

                  <Box sx={{ display: 'flex', gap: '0.5rem', whiteSpace: 'nowrap' }}>
                    <JobDetailLabel>{t('dashboard:finalQuote')}</JobDetailLabel>
                    <FacilityDuration>
                      {selectedJobDetails?.totalAmount != null
                        ? `$${selectedJobDetails?.totalAmount?.toFixed(2)}`
                        : EMPTY_VALUE}
                    </FacilityDuration>
                  </Box>

                  {selectedJobDetails?.status === JobStatus.Completed && selectedJobDetails?.actualEndDate && (
                    <Box sx={{ display: 'flex', gap: '0.5rem', whiteSpace: 'nowrap' }}>
                      <JobDetailLabel>{t('dashboard:jobCompletionDate')}</JobDetailLabel>
                      <FacilityDuration>{formatDate(selectedJobDetails.actualEndDate)}</FacilityDuration>
                    </Box>
                  )}
                  {customerId === velocitiOrganizationId && (
                    <>
                      <Box sx={{ display: 'flex', gap: '0.5rem', whiteSpace: 'nowrap', alignItems: 'center' }}>
                        <JobDetailLabel>{t('dashboard:workTicketNumber')}</JobDetailLabel>

                        <FacilityDuration>{selectedJobDetails?.workTicketNo ?? EMPTY_VALUE}</FacilityDuration>
                      </Box>
                      {selectedJobDetails?.workTicketStatus && (
                        <Box sx={{ display: 'flex', gap: '0.5rem', whiteSpace: 'nowrap', alignItems: 'center' }}>
                          <JobDetailLabel>{t('dashboard:workTicketStatus')}</JobDetailLabel>

                          <FacilityDuration>
                            {WORK_TICKET_STATUS_LABEL[selectedJobDetails?.workTicketStatus] ?? EMPTY_VALUE}
                          </FacilityDuration>
                        </Box>
                      )}
                    </>
                  )}
                </JobDetailsWrapper>
              </Box>
            );
          })
        ) : isJobSummaryError ? (
          <ErrorText />
        ) : (
          <Loader
            sx={{
              marginTop: '1.5rem',
              fontSize: theme.typography.h3?.fontSize,
              fontWeight: theme.typography.fontWeightBold,
            }}>
            {t('dashboard:noServicesFound')}
          </Loader>
        )}
      </FacilityShiftDetails>
    );
  };

  const {
    FacilityIcon = Icons.BuildingImg,
    buildingName = '',
    address: { addressLine1 = '', addressLine2 = '', addressLine3 = '', city = '', county = '', stateCode = '' } = {},
  } = selectedFacility || {};

  useEffect(() => {
    if (!isEmpty(selectedJob) && !isEmpty(selectedFacility) && !isEmpty(jobSummaryResponseData)) {
      const job = allJobs.find((data: any) => data.jobId === selectedJob);
      if (job?.vendorId) {
        fetchVendorDetails(job?.vendorId as string);
      } else {
        setVendorLoading(false);
        setVendorDetails(undefined);
      }
      if (!isEmpty(selectedFacility)) {
        fetchServices(selectedJob, selectedFacility?.customerId || location.state.customerId || '');
      }
    } else if (
      !isFirstLoad.current &&
      isEmpty(selectedJob) &&
      !isEmpty(selectedOriginalJobData) &&
      !isEmpty(selectedFacility) &&
      !isEmpty(jobSummaryResponseData)
    ) {
      setStatus(originalStatus);
      if (selectedOriginalJobData?.vendorId) {
        fetchVendorDetails(selectedOriginalJobData?.vendorId);
      } else {
        setVendorLoading(false);
        setVendorDetails(undefined);
      }
      if (!isEmpty(selectedFacility)) {
        fetchServices(location.state?.jobId, selectedFacility?.customerId || location.state.customerId || '');
      }
    }
  }, [selectedJob, selectedOriginalJobData, selectedFacility, jobSummaryResponseData]);

  const debouncedFetchAllJobs = useCallback(
    debounce((currentPage, searchString) => {
      fetchJobsForSelectedFacility(
        location.state.customerId || (selectedFacility?.customerId as string),
        currentPage,
        searchString
      );
    }, 1000),
    [currentPage, searchString]
  );

  useEffect(() => {
    if (!isEmpty(searchString?.trim()) && searchString?.length >= 3) {
      debouncedFetchAllJobs(currentPage, searchString);
    } else if (isEmpty(searchString)) {
      debouncedFetchAllJobs(currentPage, '');
    }
  }, [currentPage, searchString]);

  useEffect(() => {
    setTabSelected(initialTabIndex === -1 ? 0 : initialTabIndex);
  }, [location.pathname]);

  const fetchSelectedFacilityData = async () => {
    try {
      if (selectedJob && facilityId && selectedJobDetails?.customerId) {
        const facilityResponse = await FacilityApiService.facility(facilityId, selectedJobDetails?.customerId);
        setSelectedFacilityData(facilityResponse?.data);
        setSelectedFacility(facilityResponse?.data);
      }
    } catch (e) {
      console.log('error', e);
    }
  };

  const fetchSelectedOriginalFacilityData = async () => {
    try {
      if (!isFirstLoad.current) {
        const facilityResponse = await FacilityApiService.facility(
          originalFacilityId,
          selectedOriginalJobDetails?.customerId as string
        );
        setSelectedFacilityData(facilityResponse?.data);
        setSelectedFacility(facilityResponse?.data);
      }
    } catch (e) {
      console.log('error', e);
    }
  };
  // eslint-disable-next-line @typescript-eslint/require-await
  const fetchSelectedJobData = async (selectedJobDetails?: any) => {
    try {
      setStatus(selectedJobDetails?.status);
      if (selectedJobDetails?.startDate) {
        const startDate = new Date(selectedJobDetails?.startDate as string | number | Date);
        const endDate = new Date(selectedJobDetails?.stopDate as string | number | Date);
        const formattedStartDate = formatDate(startDate.toISOString());
        const formattedEndDate = formatDate(endDate.toISOString());
        setJobStartDate(formattedStartDate);
        setJobEndDate(formattedEndDate);
      }
      if (isFirstLoad.current) {
        setSelectedOriginalJobData(selectedJobDetails);
        setOriginalStatus(selectedJobDetails?.status);
      }
      setSelectedJobData(selectedJobDetails);
    } catch (e) {
      console.log('error', e);
    }
  };

  const getSelectedJob = () => {
    return selectedJob ? selectedJob : location.state?.jobId;
  };

  const getSelectedJobDetails = () => {
    return selectedJob ? selectedJobData : selectedOriginalJobData;
  };

  const getSelectedWorkOrder = () => {
    return selectedJob ? selectedJobData?.workOrderId : location.state?.facilityItem?.workOrderId;
  };

  const handleSearchStringChange = (value: SetStateAction<string>) => {
    setSearchString(value);
  };

  const handlePaginationChange = (event: any, value: number) => {
    setCurrentPage(value);
  };

  useEffect(() => {
    if (!jobsLoading) {
      isFirstLoad.current = false;
    }
  }, [jobsLoading]);

  const handleJobChanges = (event: any) => {
    const value = event?.target?.value;
    const label = event?.target.label;
    setSelectedJob(value);
    setSearchString(label);
  };

  const filters = [
    {
      type: FormFieldType.DropDown,
      name: FilterName.Jobs,
      options: allJobs.map((job: { label: any; value: any }) => ({
        label: job.label,
        value: job.value,
      })),
    },
  ];

  const fetchUpcomingShifts = async (workOrderResponse: any) => {
    try {
      const { jobStartDate, jobEndDate, hasChild, subWorkOrders, vendorId, workOrderId } = workOrderResponse || {};
      const payload = {
        fromDate: jobStartDate,
        toDate: jobEndDate,
        workOrderId: (hasChild ? subWorkOrders?.[0]?.workOrderId : workOrderId) ?? '',
        vendorId: (hasChild ? subWorkOrders?.[0]?.vendorId : vendorId) ?? '',
        workDayTeamStatus: WorkDayTeamStatus.A,
      };
      const upcomingShiftsResponse = await WorkOrdersApiService.getAllUpcomingShiftsDates({ ...payload });
      const modifiedUpcomingShifts = upcomingShiftsResponse?.data?.map(
        (shift: { workDate: string }) => shift?.workDate ?? ''
      );
      setFutureShiftDates(modifiedUpcomingShifts);
    } catch (e) {
      console.log('error', e);
    }
  };

  useEffect(() => {
    if (selectedJobDetails && facilityId) {
      fetchSelectedFacilityData();
    }
  }, [selectedJobDetails, facilityId]);

  useEffect(() => {
    if (isEmpty(selectedJob) && selectedOriginalJobDetails && originalFacilityId) {
      fetchSelectedOriginalFacilityData();
    }
  }, [selectedOriginalJobDetails, originalFacilityId, selectedJob]);

  const handleJobName = useCallback(() => {
    return selectedJob ? jobName : originalJobName;
  }, [selectedJob, jobName, originalJobName]);

  const getTabTitle = () => {
    return t('dashboard:teamDetails');
  };

  useEffect(() => {
    if (
      (selectedJob === location.state?.jobId && location.state?.supervisorOnTheJob) ||
      (selectedOriginalJobDetails?.isFlow2OrFlow3Job && selectedJobDetails?.isFlow2OrFlow3Job)
    ) {
      setIsPrimeVendor(true);
    } else {
      setIsPrimeVendor(false);
    }
  }, [selectedJob, selectedOriginalJobDetails, selectedJobDetails, location.state]);

  return (
    <>
      <PageTitle title={t('dashboard:jobDetails')} name={handleJobName()} />
      <CustomerContainer>
        <DropdownWrapper>
          <SearchableDropDown
            options={filters.find((filter: { name: string }) => filter.name === FilterName.Jobs)?.options || []}
            selectedValue={selectedJob}
            onChange={handleJobChanges}
            isLoading={jobsLoading}
            defaultOption={selectedJob}
            totalCount={totalCount}
            handlePaginationChange={handlePaginationChange}
            currentPage={currentPage}
            searchString={searchString}
            setSearchString={handleSearchStringChange}
            dropDownWidth={'38.75rem'}
            iconLoader={isFirstLoad.current}
          />
        </DropdownWrapper>
        <TabSection>
          <TabsWrapper>
            <Tabs
              value={tabSelected}
              onChange={handleChangeTabs}
              aria-label="basic tabs"
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile>
              {!isJobManagement && <TabTitle key={0} label={t('dashboard:overview')} value={0} {...a11yProps(0)} />}
              {status && !(status == 'Created') ? (
                <TabTitle key={1} label={t('dashboard:workTasks')} value={1} {...a11yProps(1)} />
              ) : (
                ''
              )}
              {status && !(status == 'Created') && (role as UserType) === UserType.Vendor && !isJobManagement && (
                <TabTitle key={2} label={getTabTitle()} value={2} {...a11yProps(2)} />
              )}
              {status && !(status == 'Created') && !isJobManagement && (
                <TabTitle key={3} label={t('dashboard:actionItems')} value={3} {...a11yProps(3)} />
              )}
            </Tabs>
            {/* {jobDetailsTab.length > 0 && (
              <Tabs value={tabSelected} onChange={handleChangeTabs} aria-label="basic tabs">
                {filteredTabs.map((tab, index) => (
                  <TabTitle key={tab.id} label={tab.label} value={tab.id} {...a11yProps(index)} />
                ))}
              </Tabs>
            )} */}
          </TabsWrapper>
          {isFirstLoad.current ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '200px',
              }}>
              <CircularProgress size={30} sx={{ color: theme.palette.primary.dark }} />
            </div>
          ) : (
            <>
              <TabPanel value={tabSelected} index={0}>
                {jobsLoading ? (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      minHeight: '200px',
                    }}>
                    <CircularProgress size={30} sx={{ color: theme.palette.primary.dark }} />
                  </div>
                ) : (
                  renderOverviewTab()
                )}
              </TabPanel>

              {status && !(status == 'Created') && selectedFacility ? (
                <TabPanel value={tabSelected} index={1}>
                  {screenLoading || isServicesLoading ? (
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: '200px',
                      }}>
                      <CircularProgress size={30} sx={{ color: theme.palette.primary.dark }} />
                    </div>
                  ) : (
                    <JobDetailsTasks
                      specificDate={specificDate}
                      jobStartDate={jobStartDate}
                      jobEndDate={jobEndDate}
                      control={control}
                      selectedJob={getSelectedJob()}
                      selectedFacility={selectedFacility}
                      vendorTypes={vendorTypes}
                      newDate={newDate}
                      workOrderResponseData={workOrderResponseData}
                      futureShiftDates={futureShiftDates}
                    />
                  )}
                </TabPanel>
              ) : (
                ''
              )}
              <>
                <TabPanel value={tabSelected} index={3}>
                  {jobsLoading ? (
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: '200px',
                      }}>
                      <CircularProgress size={30} sx={{ color: theme.palette.primary.dark }} />
                    </div>
                  ) : (
                    <ActionItems
                      workOrderId={getSelectedWorkOrder()}
                      workOrderResponseData={workOrderResponseData}
                      specificDate={specificDate}
                    />
                  )}
                </TabPanel>
                {status && !(status == 'Created') && (role as UserType) === UserType.Vendor && (
                  <TabPanel value={tabSelected} index={2}>
                    {jobsLoading ? (
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          minHeight: '200px',
                        }}>
                        <CircularProgress size={30} sx={{ color: theme.palette.primary.dark }} />
                      </div>
                    ) : (
                      <AssociateCard
                        specificDate={specificDate}
                        jobStartDate={jobStartDate}
                        jobEndDate={jobEndDate}
                        control={control}
                        jobData={getSelectedJobDetails()}
                        FacilityData={selectedFacilityData}
                        jobId={getSelectedJob()}
                        jobsLoader={jobsLoading}
                        workOrderResponseData={workOrderResponseData}
                        futureShiftDates={futureShiftDates}
                        isPrimeVendor={isPrimeVendor}
                        parentWoScheduleMasterId={isPrimeVendor ? parentWoScheduleMasterId : ''}
                      />
                    )}
                  </TabPanel>
                )}
              </>
            </>
          )}
        </TabSection>
        {/* <SnackBarWrapper>
          <Snackbar
            open
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            action={
              <Button
                icon={<img src={Icons.DownloadIcon} alt={t('altTexts:download')} />}
                iconDirection={IconDirection.Right}
                type={ButtonType.Primary}
                label="Download Pdf"
                onClick={() => console.log('clicked')}
              />
            }
          />
        </SnackBarWrapper> */}
      </CustomerContainer>
      {/* <ReworkTaskDetails
        openReworkTaskDetails={openReworkTaskDetails}
        handleReworkTaskDetailsClose={() => setOpenReworkTaskDetails(false)}
      /> */}
    </>
  );
});

export default JobDetailsOverview;
